import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useMemo } from "react";

import { loggedOutAuthState } from "../contexts";
import {
  IAuthState,
  IAppUser,
  IAppUserSettings,
  IStaffDepartment,
  IAuthContext,
} from "../interfaces-and-types";
import { useSwitchappSecureStore } from "./useSwitchappSecureStore";
import { appConfig } from "../configs";

export function useRehydrateEncryptedStorageData(): IAuthContext {
  const {
    getAsync,
    setAsync,
    getEncryptedAsync,
    setEncryptedAsync,
    removeAsync,
  } = useSwitchappSecureStore(appConfig.APP_DATA_STORAGE_KEY);

  const [authState, setAuthState] = useState<IAuthState>(loggedOutAuthState);

  const queryClient = useQueryClient();

  // use effect to get authState from Encrypted Storage on app start
  useEffect(() => {
    const retrieveAuthState = async () => {
      try {
        const authStateString = await getEncryptedAsync();
        // const authStateString = await getAsync();
        const rehydratedAuthState =
          authStateString && JSON.parse(authStateString);

        // console.log(
        //   "rehydratedAuthState To SET in AuthContext: ",
        //   rehydratedAuthState
        // );

        rehydratedAuthState
          ? setAuthState({
              ...rehydratedAuthState,
              app_rehydration_completed: true,
            })
          : setAuthState((prevAuthState) => ({
              ...prevAuthState,
              app_rehydration_completed: true,
            }));
      } catch (error) {
        // console.error(error);
      }
    };

    retrieveAuthState();
  }, []);

  // AUTO-persist auth state to an encrypted storage solution
  // useEffect(() => {
  //   const persistAuthState = async () => {
  //     try {
  //       // await setEncryptedAsync(JSON.stringify(authState));
  //       await setAsync(JSON.stringify(authState));
  //     } catch (error) {
  //       console.error(error);
  //     }
  //   };

  //   persistAuthState();
  // }, [
  //   // setAsync,
  //   authState.is_logged_in,
  //   authState.active_tenant_id,
  //   authState.token,
  //   authState.user?.id,
  //   authState.user_settings,
  // ]);

  const persistAuthState = async (authStateToSet: IAuthState) => {
    try {
      await setEncryptedAsync(JSON.stringify(authStateToSet));
      // await setAsync(JSON.stringify(authStateToSet));
    } catch (error) {
      console.error(error);
    }
  };

  const memoedAuthContext = useMemo(() => {
    return {
      ...authState,

      signIn: (
        staff_departments: IStaffDepartment[],
        token: string,
        user: IAppUser,
        session_id: string | null = null
      ) => {
        setAuthState((prevAuthState) => {
          const newAuthState: IAuthState = {
            ...prevAuthState,
            is_logged_in: true,
            token,
            active_tenant_id: staff_departments.length
              ? staff_departments[0].merchant_id
              : null,
            user,
            staff_departments,
            session_id,
          };

          persistAuthState(newAuthState);

          return newAuthState;
        });
      },

      signOut: () => {
        removeAsync();

        // reset queries
        queryClient.clear();
        queryClient.invalidateQueries();

        setAuthState((prevAuthState) => ({
          ...prevAuthState,
          is_logged_in: false,
          token: null,
          active_tenant_id: null,
        }));

        removeAsync();

        // setAuthState((prevAuthState) => {
        //   const newAuthState = {
        //     ...prevAuthState,
        //     is_logged_in: false,
        //     token: null,
        //     active_tenant_id: null,
        //   };

        //   persistAuthState(newAuthState);

        //   return newAuthState;
        // });
      },

      setAuthenticationState: (newAuthState: IAuthState) => {
        // console.log("newAuthState: ", newAuthState);
        setAuthState(newAuthState);
      },

      setToken: (token: string) =>
        setAuthState((prevAuthState) => ({
          ...prevAuthState,
          token,
        })),

      setApiKey: (api_key: string) =>
        setAuthState((prevAuthState) => ({
          ...prevAuthState,
          api_key,
        })),

      setStaffDepartments: (staff_departments: IStaffDepartment[]) =>
        setAuthState((prevAuthState) => {
          const newAuthState: IAuthState = {
            ...prevAuthState,
            staff_departments,
          };

          persistAuthState(newAuthState);

          return newAuthState;
        }),

      setActiveTenantId: (active_tenant_id: string) => {
        setAuthState((prevAuthState) => {
          const newAuthState: IAuthState = {
            ...prevAuthState,

            active_tenant_id,
          };

          persistAuthState(newAuthState);

          // clear all queries and refetch
          queryClient.clear();
          queryClient.invalidateQueries();

          return newAuthState;
        });
      },

      setCheckoutSessionId: (checkout_session_id: string) =>
        setAuthState((prevAuthState) => ({
          ...prevAuthState,
          checkout_session_id,
        })),

      setUser: (user: IAppUser) => {
        setAuthState((prevState) => ({ ...prevState, user }));
      },

      setUserSettings: (user_settings: IAppUserSettings) =>
        setAuthState((prevState) => ({ ...prevState, user_settings })),
    };
  }, [authState, queryClient, persistAuthState]);

  return memoedAuthContext; // unmemoedAuthContext; // memoedAuthContext;
}
